import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {CallModel} from '@app/models/call.model';
import {CallState} from '@app/models/enums/call-state.enum';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtWhitepagesService} from '@app/services/gt-whitepages.service';
import {PushEventService} from '@app/services/push-event/push-event.service';
import {CallRegistry} from '@app/services/call-registry';
import {AppUtils} from '@app/shared/utils/app-utils';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {environment} from '@environment';
import {AttendantService, CallControlService} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, forkJoin} from 'rxjs';
import {filter, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MainNumberCallsService {
  callRegistry: BehaviorSubject<CallRegistry>;

  constructor(
    private authenticationService: GtAuthenticationService,
    private attendantService: AttendantService,
    private notificationService: GtNotificationService,
    private router: Router,
    private translateService: TranslateService,
    private callControl: CallControlService,
    private pushEventService: PushEventService,
    private directoryService: GtDirectoryService,
    private whitepagesService: GtWhitepagesService
  ) {
    if (this.authenticationService && this.authenticationService.userData) {
      this.callRegistry = new BehaviorSubject<CallRegistry>(
        new CallRegistry(authenticationService.login, this.authenticationService.userData.fullNumber)
      );
      this.subscribeToPush();
      this.initCallRegistry();
    } else {
      console.log('userData not present');
    }
  }

  private initCallRegistry() {
    const organization = this.authenticationService.oname;
    const login = this.authenticationService.login;
    const r1 = this.attendantService.getActiveCalls1(organization, login).pipe(
      map(
        r => {
          if (r.code !== 200) {
            console.error(r.message);
            return [];
          }
          const calls = r.calls.map(call => new CallModel(call));
          calls.forEach(call => this.lookupCall(call));
          return calls;
        },
        e => {
          console.error(e.message);
        }
      )
    );

    const r2 = this.attendantService.getQueuedCalls1(organization, login).pipe(
      map(
        r => {
          if (r.code !== 200) {
            console.error(r.message);
            return [];
          }
          const calls: CallModel[] = [];

          r.queuedCalls.forEach(queue => {
            if (!environment.callBackQueuesCid.includes(queue.componentId)) {
              const call = CallModel.fromQueuedCall(this.authenticationService.oname, queue);
              this.lookupCall(call);
              calls.push(call);
            }
          });
          return calls;
        },
        e => {
          console.error(e.message);
        }
      )
    );

    const r3 = this.callControl.getActiveCalls2(this.authenticationService.oname, this.authenticationService.login).pipe(
      map(r => {
        if (r.code !== 200) {
          console.error(r.message);
          return [];
        }

        return r.calls.map(c => new CallModel(c));
      })
    );

    forkJoin([r1, r2, r3]).subscribe(result => {
      const activeCalls: CallModel[] = result[0];
      const queuedCalls: CallModel[] = result[1];
      const privateCalls: CallModel[] = result[2];

      const currentMainNumberCalls = this.callRegistry.getValue();

      // Remove all calls in queuedCalls that exist in active call so we don't get duplicates
      const queuedFilteredCalls = queuedCalls.filter(call => {
        return !activeCalls.some(active => active.sessionId === call.sessionId);
      });

      // Not all private calls are fetched in the attendant API, and not all attendant calls are fetched in the call control API.
      // Hence the filter-function where we remove duplicate call entries (with same sessionId and triggerPoint).
      const calls = activeCalls
        .concat(queuedFilteredCalls)
        .concat(privateCalls)
        .filter((value, index, array) => {
          return array.map(c => `${c.sessionId}${c.triggerPoint}`).indexOf(`${value.sessionId}${value.triggerPoint}`) === index;
        });

      currentMainNumberCalls.setActiveCallData(calls);
      this.callRegistry.next(currentMainNumberCalls);

      calls.forEach(call => {
        this.updateQueueStats(organization, call.serviceNumber, call.cid, currentMainNumberCalls);
      });
    });
  }

  private subscribeToPush() {
    const ps = this.pushEventService.pushSubjects;
    ps.CreateCall.subscribe(event => {
      if (event.agent_login === this.authenticationService.login) {
        const calls = this.callRegistry.getValue();
        const newCall = calls.handleCreateCall(event);
        this.lookupCall(newCall);
        this.notifyIncomingCall(newCall);
        this.callRegistry.next(calls);
      } else {
        const queuedCalls = this.callRegistry.getValue().getCallsInQueue(event.sno, event.cid);
        queuedCalls.forEach(queuedCall => {
          if (queuedCall.sessionId === event.session_id && queuedCall.agentLogin !== this.authenticationService.login) {
            queuedCall.interactionDisabled = true;
          }
        });
      }
    });
    ps.AnsweredCall.pipe(filter(event => '' + event.agent_login === this.authenticationService.login)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleAnsweredCall(event);
      this.callRegistry.next(calls);
      this.updateQueueStats(event.oname, event.sno, event.cid, calls);
      this.updateNotifications(this.callRegistry.getValue().getCurrentCall());
    });
    ps.DisconnectedCall.subscribe(event => {
      if (event.agent_login === this.authenticationService.login) {
        const calls = this.callRegistry.getValue();
        calls.handleDisconnectedCall(event);
        this.updateQueueStats(event.oname, event.sno, event.cid, calls);
        this.callRegistry.next(calls);
        this.updateNotifications(this.callRegistry.getValue().getCurrentCall());
      }
      const queuedCalls = this.callRegistry.getValue().getCallsInQueue(event.sno, event.cid);
      const call = queuedCalls.find(queuedCall => {
        return queuedCall.sessionId === event.session_id;
      });
      if (call) {
        call.interactionDisabled = false;
      }
    });
    ps.NoConnectCall.subscribe(event => {
      if (event.agent_login === this.authenticationService.login) {
        const calls = this.callRegistry.getValue();
        calls.handleNoConnectCall(event);
        this.callRegistry.next(calls);
        this.updateNotifications(this.callRegistry.getValue().getCurrentCall());
      }
      const queuedCalls = this.callRegistry.getValue().getCallsInQueue(event.sno, event.cid);
      const call = queuedCalls.find(queuedCall => {
        return queuedCall.sessionId === event.session_id;
      });
      if (call) {
        call.interactionDisabled = false;
      }
    });
    ps.TransferCall.pipe(filter(event => '' + event.agent_login === this.authenticationService.login)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleTransferCall(event);
      this.callRegistry.next(calls);
    });
    ps.TransferCallAnswered.pipe(filter(event => '' + event.login === this.authenticationService.login)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleTransferCallAnswered(event);
      this.callRegistry.next(calls);
    });
    ps.TransferCallEnd.pipe(filter(event => '' + event.login === this.authenticationService.login)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleTransferCallEnd(event);
      this.callRegistry.next(calls);
    });
    ps.Enqueue.subscribe(event => {
      if (!environment.callBackQueuesCid.includes(event.cid)) {
        const callRegistry = this.callRegistry.getValue();
        const newCall = callRegistry.handleEnqueue(event);
        this.lookupCall(newCall);
        this.updateQueueStats(event.oname, event.bno, event.cid, callRegistry);
        this.updateNotifications(this.callRegistry.getValue().getCurrentCall());
        this.callRegistry.next(callRegistry);
      }
    });

    ps.Dequeue.subscribe(event => {
      const callRegistry = this.callRegistry.getValue();
      callRegistry.handleDequeue(event);
      this.updateQueueStats(event.oname, event.bno, event.cid, callRegistry);
      this.callRegistry.next(callRegistry);
    });

    ps.CallToggled.subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleCallToggled(event);
      this.callRegistry.next(calls);
    });

    ps.EUCreateCall.pipe(filter(e => e.login === this.authenticationService.login && e.trigger_point !== 0)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleEUCreateCall(event);
      this.callRegistry.next(calls);
    });

    ps.EUDisconnectedCall.pipe(filter(e => e.login === this.authenticationService.login)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleEUDisconnectCall(event);
      this.callRegistry.next(calls);
    });

    ps.EUNoConnectCall.pipe(filter(e => e.login === this.authenticationService.login)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleEUNoConnectCall(event);
      this.callRegistry.next(calls);
    });

    ps.EUAnsweredCall.pipe(filter(e => e.login === this.authenticationService.login && e.trigger_point !== 0)).subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleEUAnsweredCall(event);
      this.callRegistry.next(calls);
      this.updateNotifications(this.callRegistry.getValue().getCurrentPrivateCall());
    });

    ps.CallRetrieved.subscribe(event => {
      const calls = this.callRegistry.getValue();
      calls.handleRetrieveCall(event);
      this.callRegistry.next(calls);
      this.updateNotifications(this.callRegistry.getValue().getCurrentPrivateCall());
    });
  }

  private lookupCall(call: CallModel) {
    this.whitepagesService.lookupCall(call);
  }

  private notifyIncomingCall(call: CallModel) {
    const swbViewURL = '/swb/swb-view';
    if (environment.muteCallNotifications) {
      return;
    }
    const notificationBody = this.translateService.instant('global.incoming_call', {number: GtPhoneNumberUtil.toDisplay(call.fullNumber)});

    if (!AppUtils.isOnMobile() && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        this.createNotification(notificationBody, swbViewURL);
      } else {
        Notification.requestPermission(permission => {
          if (permission === 'granted') {
            this.createNotification(notificationBody, swbViewURL);
          }
        });
      }
    }
    this.notificationService.addPageTitleNotification(this.translateService.instant('call_log.incoming'));

    if (this.router.url !== swbViewURL) {
      const handler = {
        message: notificationBody,
        action: () => {
          this.router.navigate([swbViewURL], {});
        }
      };
      this.notificationService.clickableNotification.next(handler);
    }
  }

  private createNotification(notificationBody: string, swbViewURL: string) {
    const notification = new Notification(this.translateService.instant('call_log.incoming'), {
      body: notificationBody,
      icon: './favicon.png'
    });
    notification.onclick = ev => {
      ev.preventDefault();
      window.focus();
      this.router.navigate([swbViewURL], {});
    };
  }

  private updateNotifications(call: CallModel) {
    if (!call || call.state !== CallState.Calling) {
      this.notificationService.removePageTitleNotification();
    }
  }

  private updateQueueStats(oname: string, serviceNumber: string, cid: number, callRegistry: CallRegistry) {
    const queuedCalls = callRegistry.getCallsInQueue(serviceNumber, cid);
    const calls = queuedCalls.length;
    const firstCreationTime = queuedCalls
      .map(call => call.creationTime)
      .reduce(
        (accumulated: Date, current: Date) => {
          return current && current < accumulated ? current : accumulated;
        },
        calls ? new Date() : null
      );
    console.log('updateQueueStats', oname, serviceNumber, cid, calls, firstCreationTime);
    this.directoryService.updateQueueStats(oname, serviceNumber, cid, calls, firstCreationTime);
  }
}
